import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useForm } from 'react-hook-form';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

interface LoginFormValues {
  email: string;
  password: string;
}

export const LoginPage: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>();
  const [authError, setAuthError] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const { user, loading, role } = useAuth();

  React.useEffect(() => {
    if (!loading && user && role === 'admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, role, loading, navigate]);

  const onSubmit = async (values: LoginFormValues) => {
    setAuthError(null);
    try {
      await signInWithEmailAndPassword(auth, values.email, values.password);
      // La redirection sera gérée par l'effet ci-dessus une fois le rôle chargé.
    } catch (err) {
      setAuthError(
        "Impossible de se connecter. Vérifiez l'email, le mot de passe ou les droits admin."
      );
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{ p: 4, width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Kalan - Backoffice Admin
            </Typography>
          </Box>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <TextField
              margin="normal"
              fullWidth
              label="Email"
              autoComplete="email"
              autoFocus
              {...register('email', { required: 'Email requis' })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Mot de passe"
              type="password"
              autoComplete="current-password"
              {...register('password', { required: 'Mot de passe requis' })}
              error={!!errors.password}
              helperText={errors.password?.message}
            />
            {authError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {authError}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Se connecter'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

